import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Alert,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import apiCall from '../app/API/apiService';
import apiEndpoints from '../app/API/apiEndpoints';
import { toasty } from '../Helper';

function UploadQuestionDialog({ open, onClose }) {
    const [questionType, setQuestionType] = useState('');
    const [file, setFile] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const handleTypeChange = (event) => {
        setQuestionType(event.target.value);
    };

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            // Check if file name ends with .csv
            if (selectedFile.name.toLowerCase().endsWith('.csv')) {
                setFile(selectedFile);
            } else {
                toasty('Please select a CSV file', 'error');
                event.target.value = null;
                setFile(null);
            }
        }
    };

    const handleUpload = async () => {
        if (!questionType) {
            toasty('Please select a question type', 'error');
            return;
        }
        if (!file) {
            toasty('Please select a file', 'error');
            return;
        }

        try {
            setIsLoading(true);
            const formData = new FormData();
            formData.append('csv_file', file);
            formData.append('csv_type', questionType);

            const response = await apiCall(
                "post",
                `${apiEndpoints.QUESTIONS}/upload`,
                formData,
                true // Set multipart/form-data
            );

            if (response) {
                toasty('Questions uploaded successfully', 'success');
                onClose(true);
            }
        } catch (error) {
            console.error('Error uploading questions:', error);
            const errorMessage = error.response?.data?.detail?.[0]?.msg || 'Error uploading questions';
            toasty(errorMessage, 'error');
        } finally {
            setIsLoading(false);
        }
    };

    // Reset state when dialog closes
    const handleClose = () => {
        setFile(null);
        setQuestionType('');
        onClose(false);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Upload Questions</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                    <FormControl fullWidth>
                        <InputLabel>Question Type</InputLabel>
                        <Select
                            value={questionType}
                            onChange={handleTypeChange}
                            label="Question Type"
                        >
                            <MenuItem value="QA">QA</MenuItem>
                            <MenuItem value="QAE">QAE</MenuItem>
                            
                        </Select>
                    </FormControl>

                    <Box sx={{ textAlign: 'center' }}>
                        <input
                            accept=".csv"
                            style={{ display: 'none' }}
                            id="csv-file-upload"
                            type="file"
                            onChange={handleFileChange}
                        />
                        <label htmlFor="csv-file-upload">
                            <Button
                                variant="outlined"
                                component="span"
                                startIcon={<CloudUploadIcon />}
                                sx={{
                                    borderColor: '#2c5da7',
                                    color: '#2c5da7',
                                    '&:hover': {
                                        borderColor: '#1e4c8f',
                                        backgroundColor: 'rgba(44, 93, 167, 0.04)'
                                    }
                                }}
                            >
                                Choose CSV File
                            </Button>
                        </label>
                        {file && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Selected file: {file.name}
                            </Typography>
                        )}
                    </Box>

                    <Alert severity="info">
                        Please ensure your CSV file matches the selected question type format.
                    </Alert>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button 
                    onClick={handleClose} 
                    disabled={isLoading}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleUpload}
                    variant="contained"
                    disabled={isLoading}
                    sx={{
                        backgroundColor: '#2c5da7',
                        '&:hover': {
                            backgroundColor: '#1e4c8f',
                        }
                    }}
                >
                    {isLoading ? 'Uploading...' : 'Upload'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default UploadQuestionDialog; 